<template>
  <div class="d-flex flex-column align-items-center page-wrapper">
    <img :src="require(`../brand/${this.brandConfig.assetBrand || this.brandConfig.brand}/assets/evina-img.webp`)" class="w-100">
    <div class="header-text mt-3">
      {{ this.brandConfig.headerText }}
    </div>
    <div class="sub-header-text mt-4">
      {{ this.brandConfig.subHeaderText }}
    </div>
    <div @click="onTsAndCsClicked" class="terms-and-conditions mt-4">
      Terms & Conditions
    </div>
    <button 
      class="accept-btn" 
      :disabled="pageLoading || isSubmitting" 
      :style="{ 
        backgroundColor: brandConfig.buttonColor || 'limegreen', 
        color: brandConfig.buttonTextColor || '#FFFFFF' 
      }" 
      @click="handleSubscribeButtonClick">
      <span v-if="isSubmitting" class="spinner"></span>
      <span v-else>Subscribe</span>
    </button>
<!--    <button class="decline-btn" @click="handleDecline">Decline</button>-->
  </div>
</template>

<script>
import {mapState, mapMutations} from "vuex";
import {ref} from 'vue';
import SafaricomService from "@/services/safaricomService";
import {triggerGTMEvent} from "@/functions/addGTM";
import {isMFilterCallback, handleMFilterCallback, handleMFilterFlow} from "@/functions/mFilter";
import {showBrandDebugAlert} from "@/functions/brandDebug";
import getCampaignParametersFromUrl from "@/functions/getCampaignParametersFromUrl";
import {msisdnLookup} from "@/utils/msisdnLookup";

const isLoading = ref(false);
const errorMessage = ref(null);

export default {
  computed: {
    ...mapState(['brandConfig', 'msisdn'])
  },
  data() {
    return {
      pageLoading: true,
      isSubmitting: false
    }
  },
  methods: {
    ...mapMutations(['setMsisdn']),
    onTsAndCsClicked() {
      window.open(this.brandConfig.termsAndConditionsUrl, '_blank');
    },
    async handleMFilterSubscriptionFlow() {
      return await handleMFilterFlow(this.brandConfig, this.$router);
    },
    // Formerly `handleEvinaSubscriptionFlow` — the Evina consent-URL redirect no longer works
    // (Safaricom's Evina tenant no longer has our price_points; verified against both the
    // migration and production deployments). Replaced with the OTP double-opt-in flow the
    // production Flutter client at ke.blg.gg already uses (see
    // client.battle-league-gaming-v2/lib/services/safaricom_service.dart + auth_provider.dart).
    // Kept the same method name until brand configs are re-flagged, since flowType==='evina'
    // still routes here from handleSubscribeButtonClick.
    async handleEvinaSubscriptionFlow() {
      if (!this.msisdn) {
        triggerGTMEvent('user_offnet');
        alert(`Please turn off Wifi and refresh the page.`);
        return;
      }

      triggerGTMEvent('user_onnet');

      const user = (await SafaricomService.getSafaricomUser(this.msisdn, this.brandConfig.productId)).data;

      // Already subscribed → straight to the game (preserves the old fast path).
      if (user?.Status?.toLowerCase() === 'active') {
        window.location.href = `${this.brandConfig.gameUrl}?msisdn=${this.msisdn}`;
        return;
      }

      const campaignParams = await getCampaignParametersFromUrl();

      // Queue the subscription intent the same way the old flow did — the SDP path is unchanged,
      // only the double-opt-in mechanism (Evina consent-URL → SMS OTP) has moved.
      await SafaricomService.queueUserSubscription(
        this.msisdn,
        this.brandConfig.offerCode,
        this.brandConfig.productId,
        campaignParams?.campaignId,
        'Web',
        campaignParams?.clickId,
        campaignParams?.publisherId,
        null,
        campaignParams?.googleClickId
      );

      // Ask Safaricom to SMS the user an OTP. If the request fails outright, land on the
      // generic error page — retries are one click away via the OTP screen's Resend button.
      try {
        await SafaricomService.requestOtp(
          this.msisdn,
          this.brandConfig.productId,
          this.brandConfig.offerCode
        );
      } catch (error) {
        console.error('requestOtp failed:', error?.response?.data || error?.message);
        this.$router.push({name: 'Error'});
        return;
      }

      triggerGTMEvent('otp_requested');
      this.$router.push({name: 'SubmitOtp'});
    },

    initializeMFilterLandingPage() {
      showBrandDebugAlert(
        this.brandConfig,
        'Page Mounted\n\n' +
        'URL: ' + window.location.href + '\n' +
        'brand: ' + this.brandConfig.brand + '\n' +
        'flowType: ' + this.brandConfig.flowType + '\n' +
        'offerCode: ' + this.brandConfig.mFilterOfferCode + '\n' +
        'redirectUrl: ' + this.brandConfig.mfilterRedirectUrl + '\n' +
        'isCallback: ' + isMFilterCallback() + '\n' +
        'localStorage.mfilter_request_id: ' + localStorage.getItem('mfilter_request_id') + '\n' +
        'localStorage.mfilter_mf_id: ' + localStorage.getItem('mfilter_mf_id') + '\n' +
        'localStorage.mfilter_msisdn: ' + localStorage.getItem('mfilter_msisdn')
      );
      if (isMFilterCallback()) {
        return handleMFilterCallback(this.$router, this.brandConfig);
      }
      this.pageLoading = false;
      return;
    },
    async initializeEvinaLandingPage() {
      if (this.msisdn) {
        showBrandDebugAlert(
            this.brandConfig,
            'Landing MSISDN available\n\n' +
            'msisdn: ' + this.msisdn + '\n' +
            'source: store'
        );
        return;
      }
      const response = await msisdnLookup()
      if (response?.msisdn && !this.msisdn) {
        this.setMsisdn(response.msisdn);
        showBrandDebugAlert(
            this.brandConfig,
            'Landing MSISDN retrieved\n\n' +
            'msisdn: ' + response.msisdn + '\n' +
            'source: header enrichment / e-msisdn'
        );
      }
      else if (response?.error) alert(response.customerMessage);
    },
    async handleSubscribeButtonClick() {
      if (this.isSubmitting) return;

      this.isSubmitting = true;
      triggerGTMEvent('subscribe_button_clicked');
      isLoading.value = true;
      errorMessage.value = null;

      try {
        if (this.brandConfig?.flowType === 'mfilter') {
          return await this.handleMFilterSubscriptionFlow();
        }

        return await this.handleEvinaSubscriptionFlow();
      } catch (error) {
        errorMessage.value = error.response?.data?.message || error.message || "An unknown error occurred.";
        this.$router.push({name: 'Error'});
      } finally {
        isLoading.value = false;
        this.isSubmitting = false;
      }
    },
  },
  async beforeMount() {
    try {
      if (this.brandConfig?.flowType === 'mfilter') return this.initializeMFilterLandingPage();
      else await this.initializeEvinaLandingPage();
    } finally {
      this.pageLoading = false;
    }
  },
  mounted() {
    triggerGTMEvent('landing_page_view');
  }
}
</script>

<style scoped>
.page-wrapper {
  height: 100vh;
}

.header-text {
  color: #FFFFFF;
  text-align: center;
  font-size: 1.2rem;
  font-weight: bold;
}

.sub-header-text {
  color: #FFFFFF;
  text-align: center;
  font-size: 0.8rem;
}

.terms-and-conditions {
  color: #FFFFFF;
  text-align: center;
  font-size: 0.7rem;
  text-decoration: underline;
}

.accept-btn {
  background-color: limegreen;
  color: #FFFFFF;
}

.decline-btn {
  background-color: #424242;
  color: #FFFFFF;
}

h1 {
  margin-bottom: 0.5rem;
  color: #333;
}

p {
  color: #666;
  line-height: 1.6;
}

button {
  padding: 15px 30px;
  font-size: 1.4rem;
  cursor: pointer;
  margin-top: 1.5rem;
  transition: background-color 0.3s, transform 0.2s;
  width: 80%;
  border: none;
}

button:hover:not(:disabled) {
  transform: translateY(-2px);
}

button:disabled {
  background-color: #9e9e9e !important;
  color: #f5f5f5 !important;
  cursor: not-allowed;
  transform: none;
  opacity: 0.7;
}

.error-message {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
  border-radius: 8px;
  text-align: left;
}

.error-message p {
  margin: 0;
  color: #c62828;
}

.footer {
  margin-top: 2rem;
  font-size: 0.8rem;
  color: #999;
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #FFFFFF;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
